import { defineStore } from 'pinia';
import { API_URL, authFetch } from '../api';

export const useLoanStore = defineStore('loans', {
  state: () => ({
    loans: [],
    loading: false,
    error: null,
  }),
  actions: {
    async fetchLoans() {
      this.loading = true;
      try {
        const response = await authFetch(`${API_URL}/loans`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        this.loans = await response.json();
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addLoan(loan) {
      try {
        // Use the same date handling as snapshotStore
        const date = new Date(loan.start_date);
        const start_date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        const endDate = new Date(loan.end_date);
        const end_date = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

        const transformedLoan = {
          ...loan,
          start_date,
          end_date
        };
        
        const response = await authFetch(`${API_URL}/loans`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(transformedLoan),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const newLoan = await response.json();
        this.loans.push(newLoan);
        return newLoan;
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async addPayment(loanId, payment) {
      try {
        const transformedPayment = {
          ...payment,
          date: payment.date ? new Date(payment.date).toISOString().split('T')[0] : null,
        };

        const response = await authFetch(`${API_URL}/loans/${loanId}/payments/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(transformedPayment),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        await this.fetchLoans(); // Refresh loan data to update totals
        return await response.json();
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async deleteLoan(loanId) {
      try {
        const response = await authFetch(`${API_URL}/loans/${loanId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.loans = this.loans.filter(loan => loan.id !== loanId);
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    async updateLoan(loanId, loan) {
      try {
        // Use the same date handling for update
        const transformedLoan = { ...loan };
        
        if (loan.start_date) {
          const date = new Date(loan.start_date);
          transformedLoan.start_date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
        
        if (loan.end_date) {
          const date = new Date(loan.end_date);
          transformedLoan.end_date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }

        const response = await authFetch(`${API_URL}/loans/${loanId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(transformedLoan),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const updatedLoan = await response.json();
        const index = this.loans.findIndex(l => l.id === loanId);
        if (index !== -1) {
          this.loans[index] = updatedLoan;
        }
        return updatedLoan;
      } catch (error) {
        this.error = error;
        throw error;
      }
    }
  }
});